/**
 * Phase 8 — Lead Form Submissions Panel
 * Reps see all submissions on decks they own; admins can switch to "All reps".
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Inbox, Search, Download, Mail, ExternalLink, Reply, Eye, RefreshCw, Trash2, ChevronRight, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { SubmissionDetailDialog } from '../components/lead-submissions/SubmissionDetailDialog';
import { ConvertToOpportunityDialog } from '../components/lead-submissions/ConvertToOpportunityDialog';

const STATUS_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'read', label: 'Read' },
  { id: 'converted', label: 'Converted' },
];

function smartDisplay(fields, ...patterns) {
  if (!fields) return '';
  for (const p of patterns) {
    for (const [k, v] of Object.entries(fields)) {
      if (k.toLowerCase().includes(p) && v) return String(v).trim();
    }
  }
  return '';
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Date.now() - d.getTime());
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

export default function LeadSubmissions() {
  const api = useApi();
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [ownerScope, setOwnerScope] = useState('me');
  const [skip, setSkip] = useState(0);
  const [detailRow, setDetailRow] = useState(null);
  const [convertRow, setConvertRow] = useState(null);
  const [convertedToast, setConvertedToast] = useState(null); // { opportunity_id }
  const limit = 50;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        skip: String(skip),
        limit: String(limit),
      });
      if (search) params.set('search', search);
      if (isAdmin && ownerScope === 'all') params.set('owner', 'all');
      const res = await api.get(`/lead-submissions?${params.toString()}`);
      setItems(res?.submissions || []);
      setTotal(res?.total || 0);
    } catch (e) {
      toast.error('Could not load submissions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, ownerScope, skip, isAdmin]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const reload = () => fetchItems();

  const onOpenDetail = async (row) => {
    setDetailRow(row);
    if (row.status === 'unread') {
      // Auto-mark-as-read on detail open
      try {
        await api.patch(`/lead-submissions/${row.id}/status`, { status: 'read' });
        setItems((prev) => prev.map((it) => it.id === row.id ? { ...it, status: 'read' } : it));
      } catch (e) { /* silent */ }
    }
  };

  const onToggleStatus = async (row) => {
    const next = row.status === 'unread' ? 'read' : 'unread';
    try {
      await api.patch(`/lead-submissions/${row.id}/status`, { status: next });
      setItems((prev) => prev.map((it) => it.id === row.id ? { ...it, status: next } : it));
      toast.success(`Marked ${next}`);
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  const onDelete = async (row) => {
    if (!isAdmin) return;
    if (!window.confirm('Soft-delete this submission? Audit trail kept; row hidden from lists.')) return;
    try {
      await api.del(`/lead-submissions/${row.id}`);
      setItems((prev) => prev.filter((it) => it.id !== row.id));
      toast.success('Submission archived');
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const onExport = () => {
    const params = new URLSearchParams({ status: statusFilter });
    if (search) params.set('search', search);
    if (isAdmin && ownerScope === 'all') params.set('owner', 'all');
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-submissions/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lead-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Export failed'));
  };

  const quickReplyMailto = (row) => {
    const email = smartDisplay(row.fields || {}, 'email');
    if (!email) {
      toast.error('No email on this submission');
      return;
    }
    const name = smartDisplay(row.fields || {}, 'name', 'full_name', 'first_name');
    const subject = encodeURIComponent(`Re: Your request via ${row.deck_title || 'our Vision Deck'}`);
    const body = encodeURIComponent(
      `Hi${name ? ` ${name.split(' ')[0]}` : ''},\n\nThanks for reaching out via ${row.deck_title || 'our deck'} — I'll be in touch shortly.\n\n— ${user?.name || ''}`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const onConvertConfirmed = (opportunityId, submissionId) => {
    setItems((prev) => prev.map((it) => it.id === submissionId ? { ...it, converted: true, opportunity_id: opportunityId, status: 'read' } : it));
    setConvertedToast({ opportunity_id: opportunityId });
    setConvertRow(null);
  };

  const headerSubtitle = useMemo(() => {
    if (loading) return 'Loading…';
    return `${total} submission${total === 1 ? '' : 's'} · ${statusFilter}`;
  }, [total, statusFilter, loading]);

  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="lead-submissions-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#B89B5E]/15">
                <Inbox className="h-6 w-6 text-[#B89B5E]" />
              </div>
              <div>
                <h1 className="font-serif text-3xl">Submissions</h1>
                <p className="text-sm text-muted-foreground">{headerSubtitle}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <select
                value={ownerScope}
                onChange={(e) => { setSkip(0); setOwnerScope(e.target.value); }}
                className="text-sm border rounded-md px-3 py-2 bg-white"
                data-testid="owner-scope-select"
              >
                <option value="me">Just me</option>
                <option value="all">All reps</option>
              </select>
            )}
            <Button variant="outline" size="sm" onClick={reload} data-testid="reload-btn"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
            <Button variant="outline" size="sm" onClick={onExport} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_CHIPS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setSkip(0); setStatusFilter(c.id); }}
                  data-testid={`filter-${c.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    statusFilter === c.id
                      ? 'bg-[#1A1A1A] text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search name, email, company, message..."
                value={search}
                onChange={(e) => { setSkip(0); setSearch(e.target.value); }}
                className="pl-9 bg-white"
                data-testid="submissions-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="text-center py-16 text-stone-500" data-testid="submissions-empty">
                <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">{loading ? 'Loading submissions…' : 'No submissions yet'}</p>
                <p className="text-xs mt-1">Lead form blocks added to your decks will land here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
                    <tr>
                      <th className="text-left px-4 py-3 w-10"></th>
                      <th className="text-left px-4 py-3">When</th>
                      <th className="text-left px-4 py-3">Deck</th>
                      <th className="text-left px-4 py-3">Name / Email</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Phone</th>
                      <th className="text-left px-4 py-3 hidden lg:table-cell">Message</th>
                      <th className="text-right px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const name = smartDisplay(row.fields || {}, 'name', 'full_name', 'first_name');
                      const email = smartDisplay(row.fields || {}, 'email');
                      const phone = smartDisplay(row.fields || {}, 'phone', 'tel', 'mobile');
                      const message = smartDisplay(row.fields || {}, 'message', 'notes', 'comment');
                      const unread = row.status === 'unread';
                      return (
                        <tr key={row.id} className={`border-t hover:bg-stone-50 transition ${unread ? 'bg-amber-50/40' : ''}`} data-testid={`submission-row-${row.id}`}>
                          <td className="px-4 py-3 align-top">
                            {unread ? (
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#B89B5E]" title="Unread" />
                            ) : (
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-stone-200" />
                            )}
                          </td>
                          <td className="px-4 py-3 align-top whitespace-nowrap text-stone-600">
                            {formatRelative(row.submitted_at)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <Link to={`/vision-decks/${row.deck_id}/edit`} className="text-stone-800 hover:text-[#B89B5E] hover:underline truncate inline-block max-w-[200px]" title={row.deck_title}>
                              {row.deck_title || '—'}
                            </Link>
                            {row.converted && (
                              <Badge variant="outline" className="ml-2 text-[10px] border-emerald-500 text-emerald-700">Converted</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className={`font-semibold ${unread ? 'text-stone-900' : 'text-stone-700'}`}>{name || <span className="opacity-50 italic">(no name)</span>}</div>
                            {email && <div className="text-xs text-stone-500">{email}</div>}
                          </td>
                          <td className="px-4 py-3 align-top hidden md:table-cell text-stone-600">{phone || '—'}</td>
                          <td className="px-4 py-3 align-top hidden lg:table-cell">
                            <span className="line-clamp-2 text-stone-600 max-w-[260px] block">{message || '—'}</span>
                          </td>
                          <td className="px-4 py-3 align-top text-right">
                            <div className="flex items-center gap-1 justify-end flex-wrap">
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onOpenDetail(row)} title="View details" data-testid={`view-${row.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => quickReplyMailto(row)} title="Quick reply" data-testid={`reply-${row.id}`}>
                                <Reply className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onToggleStatus(row)} title={row.status === 'unread' ? 'Mark read' : 'Mark unread'}>
                                <Mail className="h-4 w-4" />
                              </Button>
                              {row.converted && row.opportunity_id ? (
                                <Link to={`/opportunity/${row.opportunity_id}`} target="_blank" rel="noopener noreferrer">
                                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`view-opp-${row.id}`}>
                                    <ExternalLink className="h-3 w-3 mr-1" />Opportunity
                                  </Button>
                                </Link>
                              ) : (
                                <Button size="sm" variant="default" className="bg-[#B89B5E] hover:bg-[#A88A4D] text-white" onClick={() => setConvertRow(row)} data-testid={`convert-${row.id}`}>
                                  Convert <ChevronRight className="h-3 w-3 ml-0.5" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => onDelete(row)} title="Soft-delete">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-stone-500">Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <SubmissionDetailDialog
        open={!!detailRow}
        submission={detailRow}
        onClose={() => setDetailRow(null)}
        onConvert={() => { setConvertRow(detailRow); setDetailRow(null); }}
        onQuickReply={() => detailRow && quickReplyMailto(detailRow)}
      />
      <ConvertToOpportunityDialog
        open={!!convertRow}
        submission={convertRow}
        onClose={() => setConvertRow(null)}
        onConverted={onConvertConfirmed}
      />

      {/* Success toast / banner shown after conversion */}
      {convertedToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-white shadow-2xl border border-emerald-200 rounded-xl p-4 max-w-sm" data-testid="convert-success-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-stone-900">Opportunity created</div>
              <p className="text-sm text-stone-600 mb-2">The lead is now in your pipeline at <span className="font-medium">Discovery</span>.</p>
              <Link to={`/opportunity/${convertedToast.opportunity_id}`} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                  Open opportunity <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
            <button onClick={() => setConvertedToast(null)} className="text-stone-400 hover:text-stone-600 text-sm">×</button>
          </div>
        </div>
      )}
    </div>
  );
}
